import { GridOptions } from 'src/app/shared/classes/list/grid-options';
import { LookupData } from './../../../../../../../shared/services/lookup/lookup.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { Component, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { GridModule, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { catchError, debounceTime, filter, map, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { CollectionListWidgetComponent } from 'src/app/features/bizzmine/widgets/collection-list-widget/collection-list-widget.component';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { FilterComponent } from 'src/app/shared/components/ui/filter-component/filter.component';
import { IconComponent } from 'src/app/shared/components/ui/icon/icon.component';
import { TranslatePipe } from 'src/app/shared/pipes/translate/translate.pipe';
import { formsActions } from 'src/app/store/features/forms/forms-actions';
import { CollectionFormLookupApiService } from 'src/app/api/bizzmine/collection-form-lookup/collection-form-lookup-api.service';
import { FormType } from 'src/models/ts/form-type.model';
import { FilterItemType } from 'src/models/ts/filter-item-type.model';
import { KENDO_POPUP } from '@progress/kendo-angular-popup';
import { LinkedCollectionType } from '../../../../../../../../models/ts/linked-collection-type.model';
import { SearchComponent } from 'src/app/shared/components/ui/search/search.component';
import { ExportButtonComponent } from 'src/app/shared/components/export-button/export-button.component';

@Component({
  selector: 'bizz-instance-selection-list',
  templateUrl: '../../../../../widgets/collection-list-widget/collection-list-widget.component.html',
  styleUrls: ['../../../../../widgets/collection-list-widget/collection-list-widget.component.scss'],
  imports: [
    IconComponent,
    GridModule,
    NgClass,
    SkeletonModule,
    TranslatePipe,
    FilterComponent,
    GridComponent,
    AsyncPipe,
    KENDO_POPUP,
    SearchComponent,
    ExportButtonComponent
  ],
})
export class InstanceSelectionListComponent extends CollectionListWidgetComponent {

  @Input() public formFieldId: number;
  @Input() public formId: string;
  @Input() public isGridField: boolean;
  @Input() public sourceCollectionId: number;
  public override lookupData = input<LookupData>();
  //See ngBizzFormLookupField.js showSearchModalDeeperLevel()
  @Input() public formType: FormType;

  //TODO: RV for External Forms different listId needs to be set.
  @Output() public onNewRecord = new EventEmitter<void>();
  @Output() public onClose = new EventEmitter<boolean>();
  //@Input() public ExternalAccessList: ;
  protected override filterItemType = FilterItemType.InstanceSelector;
  private lookupApiService = inject(CollectionFormLookupApiService);
  private initialGridData: unknown[] = [];

  public override ngOnInit(): void {
    super.ngOnInit();
  }

  public override newRecord(): void {
    this.onClose.emit(true);
    if (this.lookupData()?.linkedLevel === 1) {
      //Clear selection when creating a new record
      //this.selectionChange.emit([]);
      if (this.formFieldId !== undefined) {
        //TODO: RV (ask Jonas) should only happen once
        this.actions$.pipe(
          ofType(formsActions.formFetched, formsActions.formFetchedWithFiles, formsActions.formFetchedWithTemplate, formsActions.formFetchedWithProtectedField),
          take(1),
        ).subscribe(() => this.onNewRecord.emit());
        if (this.isGridField) {
          this.store$.dispatch(formsActions.getGridLinkedForm({
            formId: this.formId,
            collectionFieldId: undefined,
            recordId: undefined,
            gridFieldId: this.formFieldId,
            relationType: LinkedCollectionType.GridRecord,
          }));
          // this.store$.dispatch(formsActions.getFormByCollectionGridField({
          //   formFieldId: this.formFieldId!,
          //   addToViewStack: true
          // }));
        } else {
          this.store$.dispatch(formsActions.getLinkedForm({
            formFieldId: this.formFieldId,
            formId: this.formId,
            relationType: LinkedCollectionType.SingleRecord,
          }));
          // this.store$.dispatch(formsActions.getFormByCollectionField({
          //   formFieldId: this.formFieldId!,
          //   addToViewStack: true
          // }));
        }
      }
    } else {
      throw new Error('Deeper Level Create action not implemented');
    }

  }

  /**
   * Sets gridResultData$ to the observable that fetches the data from the server
   * When the list search filter updates and is not undefined,
   * we fetch the data from the server,
   * update the gridData
   * and update the totalItems and wheter we have an activeFilter
   */
  protected override initListSearch(): void {
    const lookupData = this.lookupData();
    if(lookupData == undefined){
      return;
    }
    if (lookupData.linkedLevel === 1) {
      super.initListSearch();
    } else {
      this.gridResultData$ = this.listSearchFilter$.pipe(
        filter((search) => search != undefined),
        debounceTime(300),
        tap(() => this.loading = true),
        switchMap((search) => this.lookupApiService.getDeeperLevelLookupList(
            lookupData.parentFormsId,
            lookupData.childOriginalCollectionsId, // SourceParentCollectionsId (in VDS)
            this.sourceCollectionId, // collectionsId of the grid (deeper level make sure it's NOT a crosslink)
            lookupData.crossLinkCollectionsID, // crossLinkedCollections
            lookupData.dataDesignViewDataSourcesId,
            lookupData.listId ?? 0,
            lookupData.instancesId).pipe(
            map((value) => ({ value, search: search.Search })),
          ),
        ),
        catchError((err, caught) => {
          this.loading = false;
          return caught;
        }),
        tap(({ value, search }) => {
          value.GridOptions.selectable = this.gridOptions.selectable;
          this.gridOptions = new GridOptions(value.GridOptions);
          if (search && search.length > 0) {
            value.Records = value.Records.filter((record: any) => {
              return Object.values(record).some((field: any) => {
                return field?.toString().toLowerCase().includes(search.toLowerCase());
              });
            });
          }
          this.initialGridData = value.Records;
          this.gridData$.next(this.initialGridData.slice(0, this.gridOptions.pageSize));
          this.loading = false;
        }),
        switchMap(({ value }) => {
          return of({
            totalItems: value.Records.length, //TODO: Check if this is correct
            activeFilter: false,
          });
        }),
        shareReplay(1));
    }
  }

  public override onPageChange(event: PageChangeEvent): void {
    if (this.lookupData()?.linkedLevel === 1) {
      super.onPageChange(event);
      return;
    }
    this.currentPage = (event.skip / event.take) + 1;
    this.gridOptions.pageSize = event.take;
    const startIndex = (this.currentPage - 1) * this.gridOptions.pageSize;
    this.gridData$.next(this.initialGridData.slice(
      startIndex, startIndex + this.gridOptions.pageSize));
  }

}
